<template>
  <div class="max-w-7xl mx-auto mt-8 bg-white p-6 rounded-xl shadow-md">

      <!-- Header with session, user, and role -->
      <!-- min-h reserves space for wrapped names + badges so they don't shift the page -->
      <div class="flex items-center justify-between mb-4 min-h-[6rem]">
        <div class="flex items-center min-w-0">
          <h2 class="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <span>
              {{ session?.title }} from
              <span v-if="!anonymousMode">{{ user?.first_name }} {{ user?.last_name }}</span>
            </span>
            <BaseInfoIcon :text="ERKLAERUNG.benutzerDetail" label="Benutzer-Bewertungsansicht" />
          </h2>

          <!-- Badges stay on one line next to the title, never on a third line -->
          <div class="flex items-center space-x-2 shrink-0 ml-2">
            <!-- Current role display -->
            <span v-if="currentRole && !anonymousMode" class="px-2 py-1 bg-indigo-100 text-indigo-800 rounded font-semibold text-sm whitespace-nowrap">
              {{ currentRole.name }}
            </span>
            <!-- Team display -->
            <span
              v-if="currentTeam && !anonymousMode"
              class="px-2 py-1 bg-green-100 text-green-800 rounded font-semibold text-sm whitespace-nowrap"
            >
              {{ currentTeam?.name }}
            </span>
            <span
              v-else-if="!anonymousMode"
              class="px-2 py-1 bg-gray-100 text-gray-500 rounded font-semibold text-sm whitespace-nowrap"
            >
              No Team yet
            </span>
          </div>
        </div>

        <div class="flex items-center space-x-4 shrink-0">
          <!-- Toggle for Live / Async Sessions -->
          <div class="flex items-center space-x-2">
            <BaseInfoIcon :text="ERKLAERUNG.liveAsyncUmschalter" label="Live/Async-Umschalter" :oben="false" />
            <span class="text-sm font-semibold text-gray-700">Live</span>
            <label class="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" v-model="showAsyncSessions" class="sr-only peer">
              <div class="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-indigo-600 peer-focus:ring-2 peer-focus:ring-indigo-500 transition-all"></div>
              <div class="absolute left-1 top-1 w-4 h-4 bg-white rounded-full shadow-md transform transition-all peer-checked:translate-x-5"></div>
            </label>
            <span class="text-sm font-semibold text-gray-700">Async</span>
          </div>

          <!-- Toggle for Anonymous Mode -->
          <div class="flex items-center space-x-2">
            <BaseInfoIcon :text="ERKLAERUNG.anonymModus" label="Anonymous-Modus" :oben="false" />
            <span class="text-sm font-semibold text-gray-700">Anonymous</span>
            <label class="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" v-model="anonymousMode" class="sr-only peer">
              <div class="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-indigo-600 peer-focus:ring-2 peer-focus:ring-indigo-500 transition-all"></div>
              <div class="absolute left-1 top-1 w-4 h-4 bg-white rounded-full shadow-md transform transition-all peer-checked:translate-x-5"></div>
            </label>
          </div>

          <div class="border-l border-gray-300 h-6 mx-2"></div>

          <!-- Shortcuts Info Button -->
          <button
            @click="showShortcutModal = true"
            class="p-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            title="View Keyboard Shortcuts"
          >
            <InformationCircleIcon class="h-5 w-5" />
          </button>

          <!-- Change Role Button -->
          <BaseButton
            variant="edit"
            @click="showRoleModal = true"
            tooltip="Change Role"
          >
            Change Role
          </BaseButton>
          <BaseInfoIcon :text="ERKLAERUNG.rolleWechseln" label="Change Role" :oben="false" />
        </div>
      </div>

      <!-- Session & Team Controls -->
      <div class="mb-4 flex flex-col md:flex-row md:items-end md:gap-6">
        <!-- Add Session -->
        <div class="w-full md:w-64">
          <label class="mb-2 font-semibold flex items-center gap-1">
            Session
            <BaseInfoIcon :text="ERKLAERUNG.sessionHinzufuegen" label="Add session" />
          </label>
          <BaseSelect
            :options="availableSessionOptions"
            placeholder="Add session"
            :showPlaceholder="true"
            @update:modelValue="addSession"
          />
        </div>
        <!-- Team Filter -->
        <div v-if="activeSessionId" class="w-full md:w-64">
          <label class="mb-2 font-semibold flex items-center gap-1">
            Team
            <BaseInfoIcon :text="ERKLAERUNG.teamFilter" label="Team-Filter" />
          </label>
          <BaseSelect
            v-model="selectedTeamFilter"
            :options="teamFilterOptions"
            placeholder="All teams"
          />
        </div>
      </div>
      <!-- Selected Sessions -->
      <div v-if="selectedSessions.length" class="mb-6">
        <div class="flex flex-wrap gap-2">

          <div
            v-for="s in selectedSessions"
            :key="s.value"
            @click="setActiveSession(s.value)"
            :class="[
              'flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium cursor-pointer transition border',
              activeSessionId === s.value
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
            ]"
          >
            {{ s.label }}

            <button
              class="ml-1 text-xs opacity-70 hover:opacity-100"
              @click.stop="removeSession(s.value)"
            >
              ✕
            </button>
          </div>

        </div>
      </div>

      <h3 v-if="selectedUserId && currentRole && !showAsyncSessions" class="text-xl font-semibold text-gray-700 mb-4">Criteria</h3>

      <div v-if="!selectedUserId" class="text-center p-6 bg-gray-100 rounded-lg">
        <p class="text-gray-500 mb-4 text-lg font-medium">Please select a user to continue.</p>
      </div>

      <div v-else-if="!currentRole" class="text-center p-6 bg-gray-100 rounded-lg">
        <p class="text-gray-500 mb-4">This user has no role yet for {{ session?.title }}.</p>
        <BaseButton @click="showRoleModal = true">Assign Role</BaseButton>
      </div>

      <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <!-- Toggle User / Team mode (only for Live sessions) -->
        <div v-if="!showAsyncSessions" class="flex items-center space-x-3 ml-4">
          <BaseInfoIcon :text="ERKLAERUNG.bewertungsModus" label="User/Team Assessment" />
          <span class="text-sm font-semibold text-gray-700">User Assessment</span>
          <label class="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" v-model="teamMode" class="sr-only peer" />
            <div class="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-green-600 transition-all"></div>
            <div class="absolute left-1 top-1 w-4 h-4 bg-white rounded-full shadow-md transform transition-all peer-checked:translate-x-5"></div>
          </label>
          <span class="text-sm font-semibold text-gray-700">Team Assessment</span>
        </div>
        
        <!-- Criteria List (only for Live sessions) -->
        <div v-if="!showAsyncSessions && criteria.length" class="col-span-full">
          <div v-if="teamMode && teamMembers.length" class="ml-4 mt-1 text-sm text-gray-600">
            Team Members: {{ teamMembers.join(', ') }}
          </div>

          <UserCriteriaView
            class="col-span-full"
            :criteria="criteria"
            :userId="Number(selectedUserId)"
            :users="sortedFilteredUsers"
            :teams="allSessionTeams"
            :sessionUsers="userSessions"
            :teamMode="teamMode"
            :teamId="currentTeam?.id || null"
            :sessionId="Number(activeSessionId)"
            :anonymousMode="anonymousMode"
            :modalOpen="showRoleModal || showShortcutModal || assessmentModalOpen"
            @updated="fetchData()"
            @select-user="handleUserSelect"
            @select-team="handleTeamSelect"
            @assessment-modal-change="assessmentModalOpen = $event"
          />
        </div>
        
        <!-- User List for Async sessions -->
        <div v-if="showAsyncSessions" class="col-span-full">
          <div
            class="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900"
            role="status"
          >
            The async page is still under development. Some features or displayed data may be incomplete or change.
          </div>
          <UserList
            v-if="!anonymousMode"
            ref="asyncUserList"
            :users="sortedFilteredUsers"
            :selected="Number(selectedUserId)"
            :is-focused="keyboardFocusAsync"
            @select="handleUserSelect"
          />
        </div>
        
        <p v-else-if="!showAsyncSessions && !criteria.length && !currentTeam && !teamMode" class="text-gray-500 mt-4 text-center">
          The current User is not a member of a Team in this Session.
        </p>
        <p v-else-if="!showAsyncSessions && !criteria.length" class="text-gray-500 mt-4 text-center">
          No criteria assigned to this user.
        </p>
      </div>

      <AsyncMessagesSummary
        v-if="showAsyncSessions && selectedUserId && activeSessionId"
        :userId="selectedUserId"
        :sessionId="activeSessionId"
      />

      <!-- Session Feedback Section -->
      <div v-if="selectedUserId && currentRole" class="mt-8">
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center space-x-4">
            <h3 class="text-xl font-semibold text-gray-700">Session Feedback</h3>
            <span
              v-if="currentLlmModel"
              class="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-700"
            >
              Model: {{ currentLlmModel }}
            </span>
            <!-- Include in Average Toggle -->
            <div v-if="userSession" class="flex items-center space-x-2 ml-4">
              <span class="text-sm font-semibold text-gray-600">Include User in Avg:</span>
              <label class="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" v-model="includeInAverage" @change="toggleIncludeInAverage" class="sr-only peer">
                <div class="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-green-600 peer-focus:ring-2 peer-focus:ring-green-500 transition-all"></div>
                <div class="absolute left-1 top-1 w-4 h-4 bg-white rounded-full shadow-md transform transition-all peer-checked:translate-x-5"></div>
              </label>
            </div>
          </div>
          <div class="flex gap-2">
            <BaseButton 
              variant="primary" 
              @click="generateAIEvaluation(false)"
              :disabled="isGeneratingEvaluation || showAsyncSessions"
            >
              <span v-if="!isGeneratingEvaluation">Generate AI (LLM)</span>
              <span v-else-if="isGeneratingForCurrentUser" class="flex items-center">
                <ArrowPathIcon class="animate-spin h-5 w-5 mr-2" />
                Generating...
              </span>
              <span v-else-if="isGeneratingForDifferentUser" class="flex items-center">
                <ArrowPathIcon class="animate-spin h-5 w-5 mr-2" />
                Generating for {{ generatingForUserName }} (Session {{ generatingForSessionId }})...
              </span>
            </BaseButton>
            <BaseButton 
              variant="secondary" 
              @click="generateAIEvaluation(true)"
              :disabled="isGeneratingEvaluation || showAsyncSessions"
              tooltip="Uses similar past evaluations as context"
            >
              <span v-if="!isGeneratingEvaluation">Generate AI (RAG)</span>
              <span v-else-if="isGeneratingForCurrentUser" class="flex items-center">
                <ArrowPathIcon class="animate-spin h-5 w-5 mr-2" />
                Generating...
              </span>
              <span v-else-if="isGeneratingForDifferentUser" class="flex items-center">
                <ArrowPathIcon class="animate-spin h-5 w-5 mr-2" />
                Generating for {{ generatingForUserName }} (Session {{ generatingForSessionId }})...
              </span>
            </BaseButton>
          </div>
        </div>
        <span
          v-if="estimatedUserTime"
          class="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800 mb-4"
        >
          Estimated time: {{ estimatedUserTime }}
        </span>

        <!-- AI Evaluation Section (shown when AI generates evaluation OR loaded suggestion exists) -->
        <AIEvaluationSection
          v-if="pendingEvaluation || loadedSuggestion"
          :evaluation="pendingEvaluation || loadedSuggestion?.evaluation"
          :individualEvaluation="loadedSuggestion?.individual_evaluation || pendingEvaluation"
          :teamEvaluation="loadedSuggestion?.team_evaluation"
          :recommendation="pendingRecommendation || loadedSuggestion?.recommendation"
          :userHistoryCount="pendingUserHistoryCount || 0"
          :teamHistoryCount="pendingTeamHistoryCount || 0"
          :pipelineUsed="pendingPipelineUsed || loadedSuggestion?.pipeline_used || 'Unknown'"
          :performanceTimings="pendingPerformanceTimings"
          :userHistory="loadedSuggestion?.user_history || []"
          :teamHistory="loadedSuggestion?.team_history || []"
          :approved="loadedSuggestion?.approved"
          :generationStartedAt="loadedSuggestion?.generation_started_at"
          @accept="handleAcceptEvaluation"
          @decline="handleDeclineEvaluation"
          @clear="clearSuggestion"
          @save-edit="handleSaveEdit"
          class="mb-6"
        />

        <!-- Feedback Form Section -->
        <FeedbackSection
          :key="`feedback-${selectedUserId || 'none'}-${activeSessionId || 'none'}`"
          :grade="sessionGrade"
          :goal="educationalGoal"
          :evaluation="currentEvaluation"
          :recommendation="currentRecommendation"
          :grade_released="userSession?.grade_released"
          :evaluation_released="userSession?.evaluation_released"
          :recommendation_released="userSession?.recommendation_released"
          :last-saved-at="feedbackLastSavedAt"
          :save-success-tick="feedbackSaveSuccessTick"
          @save="saveSessionFeedback"
          @dirty-change="handleFeedbackDirtyChange"
        />
      </div>

        <!-- RoleModal component -->
        <RoleModal
        v-model:isOpen="showRoleModal"
        :user="user"
        :roles="roles"
        v-model:selectedRole="selectedRoleInModal"
        @save="saveRole"
      />

      <!-- Shortcuts Modal -->
      <ShortcutModal
        :isOpen="showShortcutModal"
        @close="showShortcutModal = false"
      />

      <!-- Confirm Modal -->
      <ConfirmModal
        :isOpen="showConfirmModal"
        :title="confirmModalConfig.title"
        :message="confirmModalConfig.message"
        :confirmLabel="confirmModalConfig.confirmLabel"
        @confirm="handleConfirmAction"
        @cancel="handleConfirmCancel"
      />

      <!-- Approve Confirm Modal -->
      <ApproveConfirmModal
        :isOpen="showApproveConfirmModal"
        :title="approveConfirmModalConfig.title"
        :message="approveConfirmModalConfig.message"
        :confirmLabel="approveConfirmModalConfig.confirmLabel"
        @confirm="handleApproveConfirmAction"
        @cancel="showApproveConfirmModal = false"
      />

  </div>
</template>

<script>
import BaseButton from "@/BaseComponents/BaseButton.vue";
import BaseSelect from "@/BaseComponents/BaseSelect.vue";
import RoleModal from "@/BaseComponents/RoleModal.vue";
import ConfirmModal from "@/BaseComponents/ConfirmModal.vue";
import ApproveConfirmModal from "@/BaseComponents/ApproveConfirmModal.vue";
import UserCriteriaView from "./UserCriteriaView.vue";
import UserList from "./UserList.vue";
import FeedbackSection from "./FeedbackSection.vue";
import AIEvaluationSection from "./AIEvaluationSection.vue";
import AsyncMessagesSummary from "./AsyncMessagesSummary.vue";
import { getStudentUsers, getUser } from "@/api/users";
import { getAsyncSessions, getLiveSessions, getSession } from "@/live-sessions/api/sessions";
import { getTeamCriterias } from "@/api/teamCriteria";
import { getUserCriterias} from "@/api/criterias";
import { getRoles} from "@/live-sessions/api/roles";
import { getTeams, getUsersInTeam, getTeamsForSession } from "@/live-sessions/api/teams";
import { getUserSession, updateUserSession, listUserSessions } from "@/live-sessions/api/usersessions";
import { getUserSuggestion, clearIndividualFeedback, approveFeedback as apiApproveFeedback } from "@/live-sessions/api/aiFeedback";
import { systemSettingsApi } from "@/api/systemSettings";
import { InformationCircleIcon } from "@heroicons/vue/24/solid";
import { ArrowPathIcon } from '@heroicons/vue/24/solid';
import { useServerSentEvents } from '@/composables/useServerSentEvents';
import ShortcutModal from "./ShortcutModal.vue";
import BaseInfoIcon from "@/BaseComponents/BaseInfoIcon.vue";
import { ERKLAERUNG } from "@/async-sessions/erklaerungen";
export default { 
  components: { UserCriteriaView, UserList, BaseButton, BaseSelect, RoleModal, ConfirmModal, ApproveConfirmModal, FeedbackSection, AIEvaluationSection, AsyncMessagesSummary, InformationCircleIcon, ShortcutModal, ArrowPathIcon, BaseInfoIcon },
  data() {
    return {
      ERKLAERUNG,
      sessions: [],
      selectedSessionIds: [],
      activeSessionId: null,
      users: [],
      user: null,
      selectedUserId: "",
      criteria: [],
      showTextModal: false,
      activeCriterion: null,
      textDraft: "",
      session: null,
      roles: [],
      currentRole: null,
      currentTeam: null,
      showRoleModal: false,
      selectedRoleInModal: "",
      isGeneratingEvaluation: false,
      textModalTitle: "",
      textType: "",
      usePlaceholder: "",
      sessionGrade: null,
      educationalGoal: "",
      currentEvaluation: "",
      currentRecommendation: "",
      showAsyncSessions: localStorage.getItem("showAsyncSessions") === "true",
      customIncrementValues: {},
      teamMode: false,
      teamUsers: [],
      anonymousMode: localStorage.getItem("anonymousMode") === "true",
      sessionTeams: [],
      selectedTeamFilter: "",
      userSessions: [],
      pendingEvaluation: null,
      pendingRecommendation: null,
      pendingUserHistoryCount: 0,
      pendingTeamHistoryCount: 0,
      pendingPipelineUsed: '',
      pendingPerformanceTimings: null,
      showShortcutModal: false,
      loadedSuggestion: null,
      showConfirmModal: false,
      confirmModalConfig: {
        title: '',
        message: '',
        action: '',
        confirmLabel: 'Delete'
      },
      showApproveConfirmModal: false,
      approveConfirmModalConfig: {
        title: '',
        message: '',
        confirmLabel: 'Approve'
      },
      sseConnection: null, // Store SSE connection for real-time updates
      generatingForUserId: null, // ID of user currently being generated
      generatingForUserName: null, // Name of user currently being generated
      generatingForSessionId: null, // ID of session currently being generated
      avgTimePerUserMs: null, // Average time per user from DB for current pipeline
      currentLlmModel: null,
      includeInAverage: true,  // Track include_in_average flag
      userSession: null,
      keyboardFocusAsync: false, // Track if UserList has keyboard focus in async mode
      assessmentModalOpen: false, // Track if the assessment modal is open (blocks global shortcuts)
      hasUnsavedFeedbackChanges: false,
      feedbackLastSavedAt: null,
      feedbackSaveSuccessTick: 0,
      pendingConfirmAction: null,
      pendingRouteLeaveNext: null
    };
  },
  computed: {
    isGeneratingForCurrentUser() {
      return this.isGeneratingEvaluation && this.generatingForUserId === this.selectedUserId;
    },
    isGeneratingForDifferentUser() {
      return this.isGeneratingEvaluation && this.generatingForUserId && this.generatingForUserId !== this.selectedUserId;
    },
    estimatedUserTime() {
      if (!this.isGeneratingEvaluation || !this.avgTimePerUserMs) {
        return null;
      }

      const seconds = Math.ceil(this.avgTimePerUserMs / 1000);
      return this.formatTime(seconds);
    },
    availableSessionOptions() {
      return this.flattenedSessions.filter(
        s => !this.selectedSessionIds.includes(s.value)
      );
    },
    selectedSessions() {
      return this.flattenedSessions.filter(s =>
        this.selectedSessionIds.includes(s.value)
      );
    },
    flattenedSessions() {
      const flatten = (sessions, depth = 0) => {
        return sessions.flatMap(s => [
          { value: s.id.toString(), label: `${'— '.repeat(depth)}${s.title}` },
          ...(s.children?.length ? flatten(s.children, depth + 1) : []),
        ]);
      };
      return flatten(this.sessions);
    },
    userOptions() {
      return this.filteredUsers.map(u => ({ value: u.id.toString(), label: `${u.first_name} ${u.last_name}` }));
    },
    teamMembers() {
      return this.teamUsers?.map(u => `${u.first_name} ${u.last_name}`) || [];
    },
    teamFilterOptions() {
      return [
        { value: "", label: "-- All Teams --" },
        { value: "NO_TEAM", label: "-- No Team --" },
        ...this.sessionTeams.map(t => ({ value: t.id.toString(), label: t.name }))
      ];
    },
    filteredUsers() {
      // First filter: only users with a role_id in the active session
      let usersInSession = this.users.filter(u => {
        const userSession = this.userSessions.find(us => us.user_id === u.id);
        return userSession && userSession.role_id !== null && userSession.role_id !== undefined;
      });

      // Second filter: by team if a team filter is selected
      if (!this.selectedTeamFilter) return usersInSession;

      if (this.selectedTeamFilter === "NO_TEAM") {
        return usersInSession.filter(u => {
          const userSession = this.userSessions.find(us => us.user_id === u.id);
          return userSession && (!userSession.team_ids || userSession.team_ids.length === 0);
        });
      }

      const filteredTeamId = Number(this.selectedTeamFilter);
      return usersInSession.filter(u => {
        const userSession = this.userSessions.find(us => us.user_id === u.id);
        return userSession && userSession.team_ids && userSession.team_ids.includes(filteredTeamId);
      });
    },
    sortedFilteredUsers() {
      const sorted = [...this.filteredUsers].sort((a, b) => {
        const first = (a.first_name || "").localeCompare(b.first_name || "", "en", { sensitivity: "base" });
        if (first !== 0) return first;
        return (a.last_name || "").localeCompare(b.last_name || "", "en", { sensitivity: "base" });
      });
      console.log(
        "[UserDetail] sortedFilteredUsers:",
        sorted.map(u => `id=${u.id} first_name=${JSON.stringify(u.first_name)} last_name=${JSON.stringify(u.last_name)}`)
      );
      return sorted;
    },
    currentUserTeams() {
      // Get all teams for the current user from userSession
      if (!this.userSession || !this.userSession.team_ids) {
        return [];
      }
      return this.userSession.team_ids.map(teamId => {
        const team = this.sessionTeams.find(t => t.id === teamId);
        return team || { id: teamId, name: 'Unknown Team' };
      });
    },
    allSessionTeams() {
      // Get all unique teams from all users in the current session
      if (!this.userSessions || this.userSessions.length === 0) {
        return [];
      }
      
      const teamIdSet = new Set();
      this.userSessions.forEach(userSession => {
        if (userSession.team_ids && Array.isArray(userSession.team_ids)) {
          userSession.team_ids.forEach(teamId => teamIdSet.add(teamId));
        }
      });
      
      return Array.from(teamIdSet).map(teamId => {
        const team = this.sessionTeams.find(t => t.id === teamId);
        return team || { id: teamId, name: 'Unknown Team' };
      }).sort((a, b) => a.name.localeCompare(b.name));
    },
  },
  watch: {
    async selectedUserId(newVal, oldVal) {
      if (!newVal || !this.activeSessionId) return;
      
      // Check if there's an active generation for a different user
      if (oldVal && oldVal !== newVal) {
        await this.checkGenerationStatus(oldVal);
      }
      
      this.$router.replace({
        params: { id: newVal },
        query: {
          ...this.$route.query,
          session: this.activeSessionId,
          teamMode: this.teamMode,
        },
      });
      this.fetchData(newVal, this.activeSessionId);
      this.fetchUserSession();
      this.loadUserSuggestion();
    },
    selectedSessionIds: {
      handler(newVal) {
        this.$router.replace({
          query: {
            ...this.$route.query,
            selectedSessions: newVal.join(","),
            session: this.activeSessionId,
          },
        });
      },
      deep: true,
    },
    activeSessionId(newVal) {
      if (!newVal) return;

      this.$router.replace({
        query: {
          ...this.$route.query,
          session: newVal,
          selectedSessions: this.selectedSessionIds.join(","),
        },
      });

      // Refresh everything dependent on session
      this.fetchData();
      this.fetchUserSession();
      this.loadUserSuggestion();
      this.fetchSessionTeams().then(() => {
        // If in team mode and a team is already selected, reload its criteria
        if (this.teamMode && this.currentTeam) {
          this.handleTeamSelect(this.currentTeam.id);
        }
      });
      this.fetchUserSessions().then(() => {
        // Check if current user has a role in the new session
        this.$nextTick(() => {
          if (this.selectedUserId && !this.filteredUsers.some(u => u.id.toString() === this.selectedUserId)) {
            // Current user doesn't have a role in this session, select first user with role
            if (this.filteredUsers.length > 0) {
              this.selectedUserId = this.filteredUsers[0].id.toString();
            }
          }
        });
      });
    },
    showAsyncSessions(newVal) {
      localStorage.setItem("showAsyncSessions", newVal);
      this.fetchSessions();
    },
    teamMode(newVal) {
      this.$router.replace({
        query: {
          ...this.$route.query,
          teamMode: newVal,
        },
      });
      this.fetchData();
    },
    anonymousMode(newVal) {
      localStorage.setItem("anonymousMode", newVal);
    },
  },
  methods: {
    openUnsavedChangesConfirm(message, onConfirm) {
      this.confirmModalConfig = {
        title: "Unsaved Changes",
        message,
        action: "discardUnsavedChanges",
        confirmLabel: "Discard changes"
      };
      this.pendingConfirmAction = onConfirm;
      this.showConfirmModal = true;
    },
    requestSessionSwitch(targetSessionId, switchAction) {
      if (!targetSessionId || targetSessionId === this.activeSessionId) {
        return;
      }

      if (!this.hasUnsavedFeedbackChanges) {
        switchAction();
        return;
      }

      this.openUnsavedChangesConfirm(
        "You have unsaved feedback changes. Switch session and discard these changes?",
        () => {
          this.hasUnsavedFeedbackChanges = false;
          switchAction();
        }
      );
    },
    requestUserSwitch(targetUserId, switchAction) {
      if (!targetUserId || targetUserId === this.selectedUserId) {
        return;
      }

      if (!this.hasUnsavedFeedbackChanges) {
        switchAction();
        return;
      }

      this.openUnsavedChangesConfirm(
        "You have unsaved feedback changes. Switch user and discard these changes?",
        () => {
          this.hasUnsavedFeedbackChanges = false;
          switchAction();
        }
      );
    },
    async loadCurrentLlmModel() {
      try {
        const response = await systemSettingsApi.getLlmModels();
        this.currentLlmModel = response.data.configured_model || null;
      } catch (err) {
        this.currentLlmModel = null;
      }
    },
    handleLlmModelUpdated(event) {
      if (event && event.detail && event.detail.model) {
        this.currentLlmModel = event.detail.model;
      } else {
        this.loadCurrentLlmModel();
      }
    },
    formatTime(seconds) {
      if (seconds < 60) {
        return `${seconds} Sek.`;
      } else if (seconds < 3600) {
        const minutes = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return secs > 0 ? `${minutes} Min. ${secs} Sek.` : `${minutes} Min.`;
      }

      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return minutes > 0 ? `${hours} Std. ${minutes} Min.` : `${hours} Std.`;
    },
    addSession(sessionId) {
      if (!sessionId) return;

      const applySessionSelection = () => {
        if (!this.selectedSessionIds.includes(sessionId)) {
          this.selectedSessionIds.push(sessionId);
        }
        this.activeSessionId = sessionId;
      };

      this.requestSessionSwitch(sessionId, applySessionSelection);
    },

    removeSession(sessionId) {
      if (this.activeSessionId === sessionId) {
        const nextActiveSessionId = this.selectedSessionIds.find(id => id !== sessionId) || null;

        if (!nextActiveSessionId) {
          this.selectedSessionIds = this.selectedSessionIds.filter(
            id => id !== sessionId
          );
          this.activeSessionId = null;
          return;
        }

        const removeAndSwitch = () => {
          this.selectedSessionIds = this.selectedSessionIds.filter(
            id => id !== sessionId
          );
          this.activeSessionId = nextActiveSessionId;
        };

        this.requestSessionSwitch(nextActiveSessionId, removeAndSwitch);
        return;
      }

      this.selectedSessionIds = this.selectedSessionIds.filter(
        id => id !== sessionId
      );
    },

    setActiveSession(sessionId) {
      this.requestSessionSwitch(sessionId, () => {
        this.activeSessionId = sessionId;
      });
    },
    handleUserSelect(userId) {
      const targetUserId = userId.toString();
      this.requestUserSwitch(targetUserId, () => {
        this.selectedUserId = targetUserId;
      });
    },
    async handleTeamSelect(teamId) {
      // Find the team object
      const team = this.sessionTeams.find(t => t.id === teamId) || this.allSessionTeams.find(t => t.id === teamId);
      if (!team) return;
      
      // Update currentTeam
      this.currentTeam = team;
      
      // Fetch team members
      await this.fetchTeamUsers();
      
      // Fetch criteria for this team
      if (this.activeSessionId) {
        try {
          const critRes = await getTeamCriterias(teamId, this.activeSessionId);
          this.criteria = critRes.data.sort((a, b) =>
            a.criterion.name.localeCompare(b.criterion.name, "en", { sensitivity: "base" })
          );
        } catch (err) {
          console.error("Failed to fetch team criteria:", err);
          this.criteria = [];
        }
      }
    },
    async handleKey(e) {
      // Don't handle keys if a modal is open
      if (this.showRoleModal || this.assessmentModalOpen) return;
      if (e.altKey && e.key.toLowerCase() === "t") {
        e.preventDefault();
        this.teamMode = !this.teamMode;
        await this.fetchData();
      }

      if (e.altKey && e.key.toLowerCase() === "a") {
        e.preventDefault();
        this.anonymousMode = !this.anonymousMode;
      }

      if (e.altKey && e.key.toLowerCase() === "s") {
        e.preventDefault();
        this.switchActiveSession();
      }

      // Keyboard navigation for async mode UserList
      if (this.showAsyncSessions && !this.anonymousMode && this.sortedFilteredUsers.length > 0) {
        // Check if we're in a text input/textarea (checkbox toggles don't block navigation)
        const isTextInput = e.target.tagName === 'TEXTAREA' ||
          (e.target.tagName === 'INPUT' && e.target.type !== 'checkbox');

        // Toggle focus on Tab
        if (e.key === 'Tab' && !isTextInput) {
          e.preventDefault();
          this.keyboardFocusAsync = !this.keyboardFocusAsync;
          return;
        }

        // Navigate users with arrow keys when focused
        if (this.keyboardFocusAsync && !isTextInput) {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            const currentIdx = this.sortedFilteredUsers.findIndex(u => u.id.toString() === this.selectedUserId);
            const nextIdx = Math.min(currentIdx + 1, this.sortedFilteredUsers.length - 1);
            this.handleUserSelect(this.sortedFilteredUsers[nextIdx].id);
            this.$nextTick(() => this.$refs.asyncUserList?.scrollToSelected?.());
            return;
          }
          if (e.key === "ArrowUp") {
            e.preventDefault();
            const currentIdx = this.sortedFilteredUsers.findIndex(u => u.id.toString() === this.selectedUserId);
            const nextIdx = Math.max(currentIdx - 1, 0);
            this.handleUserSelect(this.sortedFilteredUsers[nextIdx].id);
            this.$nextTick(() => this.$refs.asyncUserList?.scrollToSelected?.());
            return;
          }
        }
      }
    },
    switchActiveSession() {
      if (!this.selectedSessions.length) return;

      const currentIndex = this.selectedSessions.findIndex(
        s => s.value === this.activeSessionId
      );

      const nextIndex = (currentIndex + 1) % this.selectedSessions.length;
      const nextSessionId = this.selectedSessions[nextIndex].value;

      this.requestSessionSwitch(nextSessionId, () => {
        this.activeSessionId = nextSessionId;
      });
    },
    async fetchSessions() {
      try {
        let res;
        if (this.showAsyncSessions) {
          res = await getAsyncSessions();
        } else {
          res = await getLiveSessions();
        }
        this.sessions = res.data;

        const selected = this.$route.query.selectedSessions;
        if (selected) {
          this.selectedSessionIds = selected.split(",").filter(id =>
            this.flattenedSessions.some(s => s.value === id)
          );
        }

        const routeSession = this.$route.query.session;
        if (routeSession && this.flattenedSessions.some(s => s.value === routeSession)) {
          this.activeSessionId = routeSession;
          if (!this.selectedSessionIds.includes(routeSession)) {
            this.selectedSessionIds.push(routeSession);
          }
        }
      } catch (err) {
        console.error("Failed to fetch sessions:", err);
        this.sessions = [];
        this.activeSessionId = "";
        this.selectedSessionIds = [];
      }
    },
    async fetchUsers() {
      const res = await getStudentUsers();
      this.users = res.data;

      const routeUserId = this.$route.params.id;

      if (routeUserId && this.users.find(u => u.id.toString() === routeUserId)) {
        this.selectedUserId = routeUserId.toString();
      } else if (this.users.length) {
        this.selectedUserId = this.users[0].id.toString();
        this.$router.replace({
          params: { id: this.selectedUserId },
          query: { ...this.$route.query }
        });
      } else {
        this.selectedUserId = "";
      }
    },
    async fetchRoles() {
      const res = await getRoles();
      this.roles = res.data;
    },
    async fetchTeams() {
      const res = await getTeams();
      this.teams = res.data;
    },
    async fetchSessionTeams() {
      if (!this.activeSessionId) {
        this.sessionTeams = [];
        return;
      }
      try {
        const res = await getTeamsForSession(this.activeSessionId);
        this.sessionTeams = res.data || [];
      } catch (err) {
        console.error("Failed to fetch session teams:", err);
        this.sessionTeams = [];
      }
    },
    async fetchUserSessions() {
      if (!this.activeSessionId) {
        this.userSessions = [];
        return;
      }
      try {
        const res = await listUserSessions(this.activeSessionId);
        this.userSessions = res.data || [];
      } catch (err) {
        console.error("Failed to fetch user sessions:", err);
        this.userSessions = [];
      }
    },
    async fetchTeamUsers() {
      if (!this.currentTeam || !this.activeSessionId) {
        this.teamUsers = [];
        return;
      }
      const res = await getUsersInTeam(this.currentTeam.id, this.activeSessionId);
      this.teamUsers = res.data;
    },
    async fetchUserSession() {
      if (!this.selectedUserId || !this.activeSessionId) {
        this.currentRole = null;
        this.currentTeam = null;
        this.userSession = null;
        this.feedbackLastSavedAt = null;
        return;
      }
      try {
        const res = await getUserSession(this.selectedUserId, this.activeSessionId);
        this.userSession = res.data;
        this.feedbackLastSavedAt = this.userSession?.updated_at || null;

        const roleId = this.userSession.role_id;
        this.currentRole = this.roles.find(r => r.id === roleId) || null;
        
        // Use first team for backward compatibility in currentTeam
        const firstTeamId = this.userSession.team_ids && this.userSession.team_ids.length > 0 
          ? this.userSession.team_ids[0] 
          : null;
        this.currentTeam = firstTeamId ? this.teams.find(t => t.id === firstTeamId) || null : null;
        
        await this.fetchTeamUsers();
        this.selectedRoleInModal = roleId?.toString() || "";

        this.educationalGoal = this.userSession.goal || "";
        this.currentEvaluation = this.userSession.evaluation || "";
        this.currentRecommendation = this.userSession.recommendation || "";
        this.sessionGrade = this.userSession.grade ?? null;
        this.includeInAverage = this.userSession.include_in_average ?? true;

      } catch (err) {
        console.error("Failed to fetch user session:", err);
        this.currentRole = null;
        this.userSession = null;
        this.feedbackLastSavedAt = null;
      }
    },
    async saveRole() {
      if (!this.selectedRoleInModal) return;

      await updateUserSession(this.selectedUserId, this.activeSessionId, {
        role_id: this.selectedRoleInModal,
      });

      await this.fetchUserSession();
      this.showRoleModal = false;
    },
    async handleUpdateTeams(teamIds) {
      try {
        await updateUserSession(this.selectedUserId, this.activeSessionId, {
          team_ids: teamIds,
        });
        
        await this.fetchUserSession();
        await this.fetchUserSessions();
        await this.fetchData();
      } catch (error) {
        console.error("Failed to update teams:", error);
        alert("Failed to update teams. Please try again.");
      }
    },
    async loadUserSuggestion() {
      if (!this.selectedUserId || !this.activeSessionId) {
        this.loadedSuggestion = null;
        return;
      }

      try {
        const response = await getUserSuggestion(this.activeSessionId, this.selectedUserId);
        if (response.data && response.data.evaluation) {
          this.loadedSuggestion = response.data;
        } else {
          this.loadedSuggestion = null;
        }
      } catch (err) {
        // If no suggestion found (404), that's okay
        if (err.response?.status !== 404) {
          console.error("Failed to load user suggestion:", err);
        }
        this.loadedSuggestion = null;
      }
    },
    async clearSuggestion() {
      if (!this.selectedUserId || !this.activeSessionId) return;

      this.confirmModalConfig = {
        title: 'Clear AI Suggestion',
        message: 'Are you sure you want to clear the AI suggestion for this user? This cannot be undone.',
        action: 'clearSuggestion'
      };
      this.showConfirmModal = true;
    },
    async executeClearSuggestion() {
      if (!this.selectedUserId || !this.activeSessionId) return;

      try {
        await clearIndividualFeedback(this.activeSessionId, this.selectedUserId);
        this.loadedSuggestion = null;
        this.pendingEvaluation = null;
        this.pendingRecommendation = null;
      } catch (err) {
        console.error("Failed to clear suggestion:", err);
        alert(`Failed to clear suggestion: ${err.message}`);
      }
    },
    handleConfirmAction() {
      this.showConfirmModal = false;
      
      if (this.confirmModalConfig.action === 'clearSuggestion') {
        this.executeClearSuggestion();
        return;
      }

      if (this.confirmModalConfig.action === 'discardUnsavedChanges') {
        const action = this.pendingConfirmAction;
        this.pendingConfirmAction = null;
        this.confirmModalConfig = { title: '', message: '', action: '', confirmLabel: 'Delete' };
        if (action) {
          action();
        }
      }
    },
    handleConfirmCancel() {
      this.showConfirmModal = false;
      this.pendingConfirmAction = null;

      if (this.pendingRouteLeaveNext) {
        const next = this.pendingRouteLeaveNext;
        this.pendingRouteLeaveNext = null;
        next(false);
      }

      this.confirmModalConfig = { title: '', message: '', action: '', confirmLabel: 'Delete' };
    },
    handleApproveConfirmAction() {
      this.showApproveConfirmModal = false;
      this.executeAcceptEvaluation();
    },
    async fetchData(userIdOverride, sessionIdOverride) {
      const userId = userIdOverride || this.selectedUserId;
      const sessionId = sessionIdOverride || this.activeSessionId;
      if (!userId || !sessionId) {
        this.user = null;
        this.session = null;
        this.criteria = [];
        this.currentRole = null;
        this.currentTeam = null;
        return;
      }

      const [userRes, sessionRes] = await Promise.all([
        getUser(userId),
        getSession(sessionId),
      ]);

      this.user = userRes.data;
      this.session = sessionRes.data;

      let critRes;
      if (this.teamMode) {
        if (!this.currentTeam) {
          this.criteria = [];
          return;
        }
        critRes = await getTeamCriterias(this.currentTeam.id, sessionId);
      } else {
        critRes = await getUserCriterias(userId, sessionId);
      }

      this.criteria = critRes.data.sort((a, b) =>
        a.criterion.name.localeCompare(b.criterion.name, "en", { sensitivity: "base" })
      );
    },
    async generateAIEvaluation(useRag = false) {
      if (!this.selectedUserId || !this.activeSessionId) return;

      // Disconnect any existing SSE connection
      this.disconnectSSE();

      this.isGeneratingEvaluation = true;
      this.avgTimePerUserMs = null;
      // Store which user we're generating for
      this.generatingForUserId = this.selectedUserId;
      this.generatingForUserName = this.user ? `${this.user.first_name} ${this.user.last_name}` : 'Unknown User';
      this.generatingForSessionId = this.activeSessionId;
      
      try {
        // Start async generation
        const url = `${process.env.VUE_APP_API_URL || '/api'}/ai-feedback/generate/user/${this.activeSessionId}/${this.selectedUserId}?use_rag=${useRag}`;
        
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to generate evaluation');
        }

        const data = await response.json();
        this.avgTimePerUserMs = data.avg_time_per_user_ms || null;
        
        // Note: user_history and team_history will come from the backend
        // after generation completes and we load the suggestion
        
        // Connect to SSE for real-time updates
        this.connectToSSE();
        
      } catch (err) {
        console.error("Failed to generate AI evaluation:", err);
        alert(`Failed to generate evaluation: ${err.message}`);
        this.isGeneratingEvaluation = false;
        this.generatingForUserId = null;
        this.generatingForUserName = null;
        this.generatingForSessionId = null;
        this.avgTimePerUserMs = null;
      }
    },
    
    connectToSSE() {
      // Don't disconnect existing SSE if it's still generating
      // This allows the SSE to continue running even when switching users
      if (this.sseConnection && !this.isGeneratingEvaluation) {
        this.sseConnection.disconnect(true);
        this.sseConnection = null;
      }
      
      // Don't create a new connection if one already exists
      if (this.sseConnection) {
        console.log('[UserDetail] SSE already connected, not creating new connection');
        return;
      }

      // Connect to SSE for the user being generated (not necessarily the currently selected user)
      const apiBaseUrl = process.env.VUE_APP_API_URL || '/api';
      const token = localStorage.getItem('token');
      const sseUrl = `${apiBaseUrl}/ai-feedback/stream/user/${this.activeSessionId}/${this.generatingForUserId}${token ? `?token=${token}` : ''}`;

      console.log('[UserDetail] connectToSSE - Connecting for user', this.generatingForUserId);

      this.sseConnection = useServerSentEvents(sseUrl, {
        connected: (data) => {
          console.log('SSE connected:', data);
        },
        complete: async (data) => {
          console.log('SSE complete event:', data);
          // Only reload if we're still viewing the same user
          if (this.selectedUserId === this.generatingForUserId) {
            await this.loadUserSuggestion();
          }
          this.isGeneratingEvaluation = false;
          this.generatingForUserId = null;
          this.generatingForUserName = null;
          this.generatingForSessionId = null;
          this.avgTimePerUserMs = null;
          if (this.sseConnection) {
            this.sseConnection.disconnect(true);
            this.sseConnection = null;
          }
        },
        error: (data) => {
          console.error('SSE error event:', data);
          this.isGeneratingEvaluation = false;
          this.generatingForUserId = null;
          this.generatingForUserName = null;
          this.generatingForSessionId = null;
          this.avgTimePerUserMs = null;
          
          // Only show alert if there's an actual error message
          // Don't show alert on reconnect failures (they happen when page is refreshed during generation)
          if (data.error_message && !data.error_message.includes('Stream ended')) {
            alert(`Generation failed: ${data.error_message}`);
          }
        }
      });

      this.sseConnection.connect();
    },
    
    disconnectSSE() {
      if (this.sseConnection) {
        this.sseConnection.disconnect(true);
        this.sseConnection = null;
      }
    },
    
    async checkForActiveGeneration() {
      // Check if any user in the current session has an active generation
      if (!this.activeSessionId) return;
      
      console.log('[UserDetail] checkForActiveGeneration - Checking for active generation');
      
      // Check all users in the current session
      for (const user of this.users) {
        try {
          const url = `${process.env.VUE_APP_API_URL || '/api'}/ai-feedback/status/user/${this.activeSessionId}/${user.id}`;
          const response = await fetch(url, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          
          if (response.ok) {
            const status = await response.json();
            if (status.is_generating) {
              console.log(`[UserDetail] Found active generation for user ${user.id}`);
              // Generation is still running for this user
              this.isGeneratingEvaluation = true;
              this.generatingForUserId = user.id;
              this.generatingForSessionId = this.activeSessionId;
              this.generatingForUserName = `${user.first_name} ${user.last_name}`;
              this.avgTimePerUserMs = status.avg_time_per_user_ms || null;
              
              // Always reconnect SSE after refresh, regardless of which user is selected
              // The SSE will continue monitoring the generating user
              console.log('[UserDetail] Reconnecting SSE for generating user');
              this.connectToSSE();
              
              return; // Stop after finding the first generating user
            }
          }
        } catch (err) {
          console.error(`Failed to check generation status for user ${user.id}:`, err);
        }
      }
      
      console.log('[UserDetail] No active generation found');
    },
    
    async checkGenerationStatus(userId) {
      // Check if there's an active generation for a specific user
      if (!this.activeSessionId || !userId) return;
      
      try {
        const url = `${process.env.VUE_APP_API_URL || '/api'}/ai-feedback/status/user/${this.activeSessionId}/${userId}`;
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const status = await response.json();
          if (status.is_generating) {
            // Generation is still running for the previous user
            // Keep the generating state but don't reconnect SSE
            // (SSE connection continues running for the original user)
            this.isGeneratingEvaluation = true;
            this.generatingForUserId = userId;
            this.generatingForSessionId = this.activeSessionId;
            this.avgTimePerUserMs = status.avg_time_per_user_ms || null;
            
            // Fetch the user name for display
            try {
              const userRes = await getUser(userId);
              this.generatingForUserName = `${userRes.data.first_name} ${userRes.data.last_name}`;
            } catch (err) {
              this.generatingForUserName = 'Unknown User';
            }
          }
        }
      } catch (err) {
        console.error('Failed to check generation status:', err);
      }
    },
    async handleAcceptEvaluation() {
      if (!this.selectedUserId || !this.activeSessionId) return;

      // Check if there's existing evaluation or recommendation that will be overwritten
      const hasExistingFeedback = this.currentEvaluation || this.currentRecommendation;
      
      // Always show confirmation modal, but adjust message based on existing feedback
      this.approveConfirmModalConfig = {
        title: 'Approve AI Suggestion',
        message: hasExistingFeedback 
          ? 'This will overwrite the existing evaluation and recommendation in the user session. Are you sure you want to continue?'
          : 'This will save the AI-generated evaluation and recommendation to the user session. Do you want to proceed?',
        confirmLabel: 'Approve'
      };
      this.showApproveConfirmModal = true;
    },
    async executeAcceptEvaluation() {
      if (!this.selectedUserId || !this.activeSessionId) return;

      try {
        // Use loadedSuggestion if available, otherwise use pending data
        const evaluation = this.loadedSuggestion?.evaluation || this.pendingEvaluation;
        const recommendation = this.loadedSuggestion?.recommendation || this.pendingRecommendation;
        
        await apiApproveFeedback(this.activeSessionId, {
          user_id: parseInt(this.selectedUserId),
          approved: true,
          evaluation: evaluation,
          recommendation: recommendation,
          user_history: this.loadedSuggestion?.user_history || [],
          team_history: this.loadedSuggestion?.team_history || []
        });
        
        // Update local state with accepted evaluation
        this.currentEvaluation = evaluation;
        this.currentRecommendation = recommendation;
        
        if (this.userSession) {
          this.userSession.evaluation = evaluation;
          this.userSession.recommendation = recommendation;
        }

        // Clear pending evaluation
        this.pendingEvaluation = null;
        this.pendingRecommendation = null;
        this.loadedSuggestion = null;
        
        // Reload suggestion to show approved status
        await this.loadUserSuggestion();
        
      } catch (err) {
        console.error("Failed to accept evaluation:", err);
        alert(`Failed to accept evaluation: ${err.message}`);
      }
    },
    async handleDeclineEvaluation() {
      if (!this.selectedUserId || !this.activeSessionId) return;

      try {
        // Use loadedSuggestion if available, otherwise use pending data
        const evaluation = this.loadedSuggestion?.evaluation || this.pendingEvaluation;
        const recommendation = this.loadedSuggestion?.recommendation || this.pendingRecommendation;
        
        await apiApproveFeedback(this.activeSessionId, {
          user_id: parseInt(this.selectedUserId),
          approved: false,
          evaluation: evaluation,
          recommendation: recommendation,
          user_history: this.loadedSuggestion?.user_history || [],
          team_history: this.loadedSuggestion?.team_history || []
        });
        
        // Clear from UI
        this.pendingEvaluation = null;
        this.pendingRecommendation = null;
        this.loadedSuggestion = null;
        
        // Reload suggestion to show declined status
        await this.loadUserSuggestion();
        
      } catch (err) {
        console.error("Failed to decline evaluation:", err);
        alert(`Failed to decline evaluation: ${err.message}`);
      }
    },
    handleSaveEdit(editedData) {
      // Update the pending or loaded suggestion with edited values
      if (this.loadedSuggestion) {
        this.loadedSuggestion.individual_evaluation = editedData.individualEvaluation;
        this.loadedSuggestion.team_evaluation = editedData.teamEvaluation;
        this.loadedSuggestion.recommendation = editedData.recommendation;
        // Reconstruct full evaluation
        let fullEvaluation = editedData.individualEvaluation;
        if (editedData.teamEvaluation && editedData.teamEvaluation.trim()) {
          fullEvaluation += `\n\n**TEAM:** ${editedData.teamEvaluation.trim()}`;
        }
        this.loadedSuggestion.evaluation = fullEvaluation;
      } else {
        // Update pending data
        this.pendingEvaluation = editedData.individualEvaluation;
        this.pendingRecommendation = editedData.recommendation;
      }
    },
    handleFeedbackDirtyChange(isDirty) {
      this.hasUnsavedFeedbackChanges = isDirty;
    },
    handleBeforeUnload(event) {
      if (!this.hasUnsavedFeedbackChanges) {
        return;
      }

      event.preventDefault();
      event.returnValue = "";
    },
    async saveSessionFeedback({ grade, goal, evaluation, recommendation, grade_released, evaluation_released, recommendation_released }) {
      if (!this.selectedUserId || !this.activeSessionId) return;

      try {
        const res = await updateUserSession(this.selectedUserId, this.activeSessionId, {
          grade,
          goal,
          evaluation,
          recommendation,
          grade_released,
          evaluation_released,
          recommendation_released
        });

        // update local values - only update if defined
        if (grade !== undefined) this.sessionGrade = grade;
        if (goal !== undefined) this.educationalGoal = goal;
        if (evaluation !== undefined) this.currentEvaluation = evaluation;
        if (recommendation !== undefined) this.currentRecommendation = recommendation;
        if (!this.userSession) this.userSession = {};
        if (grade_released !== undefined) this.userSession.grade_released = grade_released;
        if (evaluation_released !== undefined) this.userSession.evaluation_released = evaluation_released;
        if (recommendation_released !== undefined) this.userSession.recommendation_released = recommendation_released;

        const savedUserSession = res?.data;
        if (savedUserSession) {
          this.userSession = { ...this.userSession, ...savedUserSession };
          this.feedbackLastSavedAt = savedUserSession.updated_at || this.feedbackLastSavedAt;
        }

        const savedTrackedFeedbackFields =
          grade !== undefined ||
          goal !== undefined ||
          evaluation !== undefined ||
          recommendation !== undefined;

        if (savedTrackedFeedbackFields) {
          this.hasUnsavedFeedbackChanges = false;
        }
        this.feedbackSaveSuccessTick += 1;
      } catch (err) {
        console.error("Failed to save session feedback:", err);
      }
    },
    async toggleIncludeInAverage() {
      try {
        await updateUserSession(this.selectedUserId, this.activeSessionId, {
          include_in_average: this.includeInAverage
        });
        // Optionally refresh user session to confirm
        await this.fetchUserSession();
      } catch (err) {
        console.error("Failed to update include_in_average:", err);
        // Revert on error
        this.includeInAverage = !this.includeInAverage;
      }
    }
  },
  async mounted() {
    window.addEventListener("keydown", this.handleKey);
    window.addEventListener('llm-model-updated', this.handleLlmModelUpdated);
    window.addEventListener("beforeunload", this.handleBeforeUnload);

    await Promise.all([
      this.fetchSessions(),
      this.fetchUsers(),
      this.fetchRoles(),
      this.fetchTeams()
    ]);

    await this.fetchData();
    await this.fetchUserSession();
    await this.loadUserSuggestion();
    await this.fetchSessionTeams();
    await this.fetchUserSessions();
    await this.loadCurrentLlmModel();
    
    // Check if there's an active generation after loading
    await this.checkForActiveGeneration();
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.handleKey);
    // Clean up SSE connection when component is destroyed
    this.disconnectSSE();
    window.removeEventListener('llm-model-updated', this.handleLlmModelUpdated);
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
  },
  beforeRouteLeave(to, from, next) {
    if (!this.hasUnsavedFeedbackChanges) {
      next();
      return;
    }

    this.pendingRouteLeaveNext = next;
    this.openUnsavedChangesConfirm(
      "You have unsaved feedback changes. Leave page and discard these changes?",
      () => {
        this.hasUnsavedFeedbackChanges = false;
        const routeNext = this.pendingRouteLeaveNext;
        this.pendingRouteLeaveNext = null;
        if (routeNext) {
          routeNext();
        }
      }
    );
  }
};
</script>
